// Google sign-in bridge for TENANT hosts (custom domains + <label>.solunary.health
// subdomains). Google Identity Services only trusts REGISTERED JavaScript origins,
// so it cannot run directly on the open-ended set of tenant hosts — the visitor is
// instead bounced through the PLATFORM origin (the one registered origin,
// saas/AuthBridge.jsx) and back. See backend/auth_handoff.py for the server-side
// protocol and its security invariants (single-use code, 90s expiry, host-bound
// consume, anti login-CSRF nonce).
//
// Extracted, behavior-preserving, from public/ClientPortal.jsx so the same bridge
// can be reused by components/auth/ClientAuthPanel.jsx — see
// docs/auth-and-admin/client-otp-auth-contract.md.
import { api } from "@/lib/api";
import { platformOrigin } from "@/lib/tenantHost";

/**
 * beginBridgeSignin — starts the bridge from a tenant host. Stores an unforgeable
 * nonce in THIS tab's sessionStorage (required, same-origin, at /finalize — anti
 * login-CSRF, see auth_handoff.py:98-104) and navigates the whole browser to the
 * platform origin's bridge page, carrying `returnPath` (this host's path + query)
 * as where to come back to once signed in.
 *
 * @param {string} returnPath  path (+ optional query) on THIS host to return to,
 *                              e.g. `window.location.pathname + window.location.search`.
 * @param {(nonce: string) => void} [onNonceReady]  BUG-195 — called synchronously right
 *   after the nonce is persisted to sessionStorage, before the navigation fires. This is
 *   the ONLY moment a caller can hand something a value bound to THIS bridge attempt: the
 *   nonce is single-use, tab-scoped (sessionStorage, never shared across tabs even on the
 *   same origin) and already the anti-CSRF proof the server checks at /finalize. A caller
 *   with its own state to survive the redirect (Reserver.jsx's selected slot and typed
 *   details) can key what it persists to this SAME nonce, so it stays unreadable to
 *   anything that did not just go through THIS exact handoff — see
 *   lib/pendingBookingDraft.js.
 * @param {{marketingConsent?: boolean, termsAccepted?: boolean}} [options]  the sign-in
 *   panel's two boxes, posted with the nonce at /finalize (`modules/crm/signup.py`).
 */
export function beginBridgeSignin(returnPath, onNonceReady, { marketingConsent = false, termsAccepted = false } = {}) {
  const nonce = window.crypto?.randomUUID?.() || Math.random().toString(36).slice(2);
  // sessionStorage (tenant origin, THIS tab): the unforgeable proof that this
  // browser initiated the flow. Required at /finalize — anti login-CSRF.
  sessionStorage.setItem("hb_nonce", nonce);
  // The sign-in panel's "news and offers" marketing tick, carried across the bridge
  // with the nonce and posted with it at /finalize (CRM consent evidence only).
  sessionStorage.setItem("hb_marketing_consent", marketingConsent ? "1" : "0");
  sessionStorage.setItem("hb_terms_accepted", termsAccepted ? "1" : "0");
  onNonceReady?.(nonce);
  const u = new URL(returnPath, window.location.origin);
  u.searchParams.set("hb_nonce", nonce);
  window.location.assign(`${platformOrigin()}/auth/bridge?return=${encodeURIComponent(u.toString())}`);
}

/**
 * finalizeBridgeIfPending — call on mount of any page the bridge can land back on.
 * No-ops when neither `hb_finalize` nor `auth_error` is in the URL. When a finalize
 * IS pending: consumes the httpOnly `hb_code` cookie server-side
 * (POST /auth/handoff/finalize) together with the nonce this tab stored at
 * beginBridgeSignin, installs the session on success, refreshes AuthContext, and
 * strips both query params either way (never left visible / re-processed on a
 * later render).
 *
 * Callers own their own single-invocation guard (a `useRef`, exactly the pattern
 * already used in public/ClientPortal.jsx and admin/Login.jsx) — React StrictMode
 * double-invokes mount effects in dev, and the hb_code cookie is single-use
 * server-side (find_one_and_delete), so a second POST /finalize would race the
 * first with the nonce already cleared and fail.
 *
 * @param {() => Promise<void>} refresh  AuthContext's `refresh()`.
 * @returns {Promise<
 *   {status: "none"} |
 *   {status: "ok", nonce: string} |
 *   {status: "error", reason: "expired"|"invalid", nonce: string}
 * >}
 *   BUG-195 — `nonce` is the SAME value `beginBridgeSignin` handed its caller through
 *   `onNonceReady`, read here before it is cleared from sessionStorage. `status: "none"`
 *   carries none at all: neither `hb_finalize` nor `auth_error` was in the URL, so this
 *   mount never went through the bridge, and a caller gating its own state on this nonce
 *   (see lib/pendingBookingDraft.js) must treat "no nonce" as "nothing to restore" — never
 *   fall back to restoring unconditionally.
 */
export async function finalizeBridgeIfPending(refresh) {
  const params = new URLSearchParams(window.location.search);
  const err = params.get("auth_error");
  const finalize = params.get("hb_finalize");
  if (!err && !finalize) return { status: "none" };

  const strip = () => {
    params.delete("auth_error");
    params.delete("hb_finalize");
    const qs = params.toString();
    window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
  };

  // Read once, then removed either way (single-use, whether this handoff succeeds,
  // fails, or the platform side itself reported `auth_error`) — a nonce that outlives
  // this call must not gate a LATER draft read against a THEN-stale value.
  const nonce = sessionStorage.getItem("hb_nonce") || "";
  sessionStorage.removeItem("hb_nonce");
  const marketingConsent = sessionStorage.getItem("hb_marketing_consent") === "1";
  sessionStorage.removeItem("hb_marketing_consent");
  const termsAccepted = sessionStorage.getItem("hb_terms_accepted") === "1";
  sessionStorage.removeItem("hb_terms_accepted");

  if (err) {
    strip();
    return { status: "error", reason: err === "expired_code" ? "expired" : "invalid", nonce };
  }

  try {
    await api.post("/auth/handoff/finalize", {
      nonce, marketing_consent: marketingConsent, terms_accepted: termsAccepted,
    });
    await refresh(); // finalize sets the session cookie server-side
    return { status: "ok", nonce };
  } catch {
    return { status: "error", reason: "invalid", nonce }; // missing/incorrect nonce → forged link
  } finally {
    strip();
  }
}
